// src/pages/kuyumcu/KuyumcuParametrelerPage.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import KuyumcuLeftSideNavbar from "./components/KuyumcuLeftSideNavbar";
import PremiumBackground from "./components/PremiumBackground";

import ProsetDataGrid from "./components/KuyumcuDataGrid";
import ProsetPopup from "./components/ProsetPopup";
import SilmeUyariPopup from "./components/SilmeUyariPopup";

import axiosInstance from "../../api/axiosInstance";
import { getUserId } from "../../utils/getUserId";
import { usePermission } from "../../context/PermissionContext";

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";

import {
  FaSlidersH,
} from "react-icons/fa";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Edit, Delete } from "@mui/icons-material";

import "./css/KuyumcuMainPage.css";

export default function KuyumcuParametrelerPage() {
  const navigate = useNavigate();
  const userId = getUserId();
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  const { can, isLoading } = usePermission();
  // ====== PARAMETRELER / BÖLGE KOD YETKİLERİ ======
  const PERM_BOLGEKOD_VIEW = "PARAMETRELER_VIEW";
  const PERM_BOLGEKOD_CREATE = "PARAMETRELER_CREATE";
  const PERM_BOLGEKOD_UPDATE = "PARAMETRELER_UPDATE";
  const PERM_BOLGEKOD_DELETE = "PARAMETRELER_DELETE";

  const canView = !isLoading && can(PERM_BOLGEKOD_VIEW);
  const canCreate = !isLoading && can(PERM_BOLGEKOD_CREATE);
  const canUpdate = !isLoading && can(PERM_BOLGEKOD_UPDATE);
  const canDelete = !isLoading && can(PERM_BOLGEKOD_DELETE);


  useEffect(() => {
    if (!userId || !token) navigate("/login");
  }, [userId, token, navigate]);

  // ----- Bölge Kod List -----
  const [rows, setRows] = useState([]);

  // bolgeKodId -> bu bölge kodunu kullanan hesap var mı?
  const [bolgeKodHesapMap, setBolgeKodHesapMap] = useState({});

  const loadBolgeKod = async () => {
    if (!userId) return;

    if (isLoading) return;

    if (!can(PERM_BOLGEKOD_VIEW)) {
      setRows([]);
      return;
    }

    const resp = await axiosInstance.get("/parametreler/bolge-kod");
    setRows(resp.data || []);
  };

  // Bölge kodu bağımlılıkları: hesaplar içinde bolgeKodId var mı?
  const loadBolgeKodDependencies = async () => {
    if (!userId) return;

    if (isLoading) return;

    if (!can(PERM_BOLGEKOD_VIEW)) {
      setBolgeKodHesapMap({});
      return;
    }

    try {
      // Not: Hesaplar endpoint'in senin projende bu şekilde kullanılıyor:
      // GET /hesaplar?userId=...
      const resp = await axiosInstance.get("/hesaplar");
      const hesaplar = resp.data || [];

      const map = {};
      hesaplar.forEach((h) => {
        if (h?.bolgeKodId != null) {
          map[h.bolgeKodId] = true;
        }
      });

      setBolgeKodHesapMap(map);
    } catch (e) {
      setBolgeKodHesapMap({});
    }
  };

  useEffect(() => {
    loadBolgeKod();
    loadBolgeKodDependencies();
    // eslint-disable-next-line
  }, [userId, isLoading]);

  // ----- Popup States -----
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [form, setForm] = useState({
    id: null,
    bolgeKod: "",
    aciklama: "",
  });

  const [silmePopupOpen, setSilmePopupOpen] = useState(false);
  const [silinecek, setSilinecek] = useState(null);

  const openCreate = () => {
    if (isLoading || !can(PERM_BOLGEKOD_CREATE)) {
      alert("Bölge kod ekleme yetkiniz yok.");
      return;
    }
    setEditMode(false);
    setForm({ id: null, bolgeKod: "", aciklama: "" });
    setOpen(true);
  };

  const openEdit = (row) => {
    if (isLoading || !can(PERM_BOLGEKOD_UPDATE)) {
      alert("Düzenleme yetkiniz yok.");
      return;
    }
    setEditMode(true);
    setForm({
      id: row.id,
      bolgeKod: row.bolgeKod || "",
      aciklama: row.aciklama || "",
    });
    setOpen(true);
  };

  const askDelete = (row) => {
    setSilinecek(row);
    setSilmePopupOpen(true);
  };

  const save = async () => {
    if (isLoading) return;

    const needPerm = editMode ? PERM_BOLGEKOD_UPDATE : PERM_BOLGEKOD_CREATE;
    if (!can(needPerm)) {
      alert("Bu işlem için yetkiniz yok.");
      return;
    }

    if (!form.bolgeKod?.trim()) {
      alert("Bölge kod zorunludur.");
      return;
    }

    if (!editMode) {
      await axiosInstance.post("/parametreler/bolge-kod", {
        kullaniciId: userId,
        bolgeKod: form.bolgeKod.trim(),
        aciklama: form.aciklama,
      });
    } else {
      await axiosInstance.put(`/parametreler/bolge-kod/${form.id}`,
        {
          bolgeKod: form.bolgeKod.trim(),
          aciklama: form.aciklama,
        }
      );
    }

    setOpen(false);
    await loadBolgeKod();
    await loadBolgeKodDependencies();
  };

  const doDelete = async () => {
    if (!silinecek?.id) return;

    if (isLoading || !can(PERM_BOLGEKOD_DELETE)) {
      alert("Silme yetkiniz yok.");
      setSilmePopupOpen(false);
      setSilinecek(null);
      return;
    }

    // bağımlı hesap varsa silme
    if (bolgeKodHesapMap[silinecek.id]) {
      alert("Bu bölge koduna bağlı hesap bulunduğu için silinemez.");
      setSilmePopupOpen(false);
      setSilinecek(null);
      return;
    }

    await axiosInstance.delete(`/parametreler/bolge-kod/${silinecek.id}`)

    setSilmePopupOpen(false);
    setSilinecek(null);

    await loadBolgeKod();
    await loadBolgeKodDependencies();
  };

  const columns = useMemo(() => {
    return [
      { field: "bolgeKod", headerName: "Bölge Kod", flex: 1 },
      { field: "aciklama", headerName: "Açıklama", flex: 2 },

      {
        field: "duzenle",
        headerName: "",
        maxWidth: 60,
        flex: 0.6,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          if (!canUpdate) return null;

          return (
            <IconButton
              onClick={() => openEdit(params.row)}
              style={{ color: "#b8860b" }}
            >
              <Edit />
            </IconButton>
          );
        },

      },
      {
        field: "sil",
        headerName: "",
        maxWidth: 60,
        flex: 0.5,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          if (!canDelete) return null;

          const disabled = !!bolgeKodHesapMap[params.row.id];

          return (
            <Tooltip
              title={
                disabled
                  ? "Bu bölge koduna bağlı hesap bulunduğu için silinemez."
                  : "Sil"
              }
            >
              <span>
                <IconButton
                  disabled={disabled}
                  onClick={() => {
                    if (disabled) return;
                    askDelete(params.row);
                  }}
                  style={{ color: disabled ? "#aaa" : "#d32f2f" }}
                >
                  <Delete />
                </IconButton>
              </span>
            </Tooltip>
          );
        },

      },
    ];
  }, [bolgeKodHesapMap, canUpdate, canDelete]);

  return (
    <div className="main-container">
      <PremiumBackground />
      <KuyumcuLeftSideNavbar />

      <div className="content">
        <h1 style={{ color: "#b8860b", marginBottom: 16 }}><FaSlidersH/> Parametreler</h1>

        {/* 1) Bölge Kod Panel */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 800, color: "#b8860b" }}>
              Bölge Kod
            </Typography>
          </AccordionSummary>

          <AccordionDetails>
            <Box display="flex" justifyContent="flex-end" mb={2}>
              {canCreate && (
                <Button
                  variant="contained"
                  sx={{
                    background: "#b8860b",
                    "&:hover": { background: "#d4a017" },
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 800,
                  }}
                  onClick={openCreate}
                >
                  Bölge Kod Ekle
                </Button>
              )}

            </Box>

            <div style={{ height: 520 }}>
              <ProsetDataGrid
                rows={rows}
                columns={columns}
                pageSize={10}
                getRowId={(r) => r.id}
              />
            </div>
          </AccordionDetails>
        </Accordion>

        {/* Ekle / Düzenle Popup */}
        <ProsetPopup
          open={open}
          title={editMode ? "Bölge Kod Düzenle" : "Bölge Kod Ekle"}
          editMode={false} // popup içinde Sil butonu istemiyorsan
          sabitMi={true} // editMode yanlışlıkla true olsa bile Sil çıkmasın
          onCancel={() => setOpen(false)}
          onSave={save}
        >
{/*         <PremiumBackground /> */}
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Bölge Kod"
              value={form.bolgeKod}
              onChange={(e) =>
                setForm((p) => ({ ...p, bolgeKod: e.target.value }))
              }
              inputProps={{ maxLength: 30 }}
              fullWidth
            />
            <TextField
              label="Açıklama"
              value={form.aciklama}
              onChange={(e) =>
                setForm((p) => ({ ...p, aciklama: e.target.value }))
              }
              inputProps={{ maxLength: 255 }}
              fullWidth
            />
          </Box>
        </ProsetPopup>

        {/* Silme Popup */}
        <SilmeUyariPopup
          open={silmePopupOpen}
          message={
            silinecek
              ? `"${silinecek.bolgeKod}" bölge kodunu silmek istiyor musun?`
              : "Silmek istiyor musun?"
          }
          onCancel={() => {
            setSilmePopupOpen(false);
            setSilinecek(null);
          }}
          onConfirm={doDelete}
        />
      </div>
    </div>
  );
}
